'use client';

import { memo, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { NEUTRAL_CHIP, canReadLogoPixels, getCachedLogoChip, measureLogoChip } from '@/lib/brands/logoChip';

// The brand mark, tile included.
//
// `BrandLogo` renders the image and nothing else, so every caller invented its own tile: usually a
// fixed dark square with the image drawn edge to edge. That is why black wordmarks disappeared, why
// transparent marks sat on a background they were never designed for, and why no logo had a margin.
//
// This component owns the tile, so the answer is the same everywhere a brand appears. The tile
// colour is derived from the mark itself (see lib/brands/logoChip.ts) and the margin is applied once,
// here. Prefer it over `BrandLogo` wherever a brand logo is shown in a box.

const initialsOf = (name?: string | null): string =>
  (name || '?')
    .trim()
    .split(/[\s'’-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase() || '?';

type BrandMarkProps = {
  logoUrl?: string | null;
  name?: string | null;
  /** Tile edge in pixels. The mark fills 80 percent of it. */
  size?: number;
  /** Corner radius in pixels. Defaults to roughly a sixth of the size. */
  radius?: number;
  /**
   * Fade and settle in on mount. Opt-in: a shortlist table paints fifty marks at once and animating
   * those would be noise, but a card showing three wants them to arrive.
   */
  appear?: boolean;
  /** Stagger, in milliseconds, for a row of marks that appear together. Ignored unless `appear`. */
  appearDelayMs?: number;
  /** Cast a shadow, so the mark reads as a tile sitting on the card rather than a hole cut into it. */
  raised?: boolean;
  className?: string;
};

/**
 * Two stacked shadows: a tight contact shadow anchoring the tile, a wider one giving it height.
 * Exported so a caller drawing a non-brand tile beside a BrandMark (a unit's icon on the same
 * podium, say) can sit at the same height instead of guessing at it.
 */
export const BRAND_MARK_RAISED_SHADOW = '0 1px 2px rgba(0,0,0,0.4), 0 4px 10px rgba(0,0,0,0.32)';

/**
 * How the image is being loaded. `cors` is the normal path and lets the tile be measured. Most brand
 * logos live in our own container, which reflects any Origin, but 57 brands point at a third party
 * and Google's favicon endpoint sends no CORS header at all: requesting those with crossOrigin set
 * fails outright, so a mark that renders today would vanish. On that error we reload the image
 * WITHOUT crossOrigin. The logo shows, the tile stays neutral, and nothing regresses.
 */
type LoadMode = 'cors' | 'plain' | 'failed';

const IMAGE_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: '10%',
};

const BrandMarkComponent = ({
  logoUrl,
  name,
  size = 28,
  radius,
  appear = false,
  appearDelayMs = 0,
  raised = false,
  className,
}: BrandMarkProps) => {
  const url = logoUrl || null;
  // Serve a known tile colour on the very first paint so cached marks never flash neutral.
  const [chip, setChip] = useState(() => getCachedLogoChip(url));
  // Start plain for a host that cannot answer CORS, rather than asking and being refused: the
  // refusal is two console errors per logo and the outcome is the same neutral tile. See
  // canReadLogoPixels. The onError fallback below stays, for our own container failing.
  const [mode, setMode] = useState<LoadMode>(() => (canReadLogoPixels(url ?? '') ? 'cors' : 'plain'));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setMode(canReadLogoPixels(url ?? '') ? 'cors' : 'plain');
    const known = getCachedLogoChip(url);
    setChip(known);
    if (!url || known) return;
    let cancelled = false;
    void measureLogoChip(url).then((result) => {
      if (!cancelled && result) setChip(result);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const initials = useMemo(() => initialsOf(name), [name]);
  const corner = radius ?? Math.max(4, Math.round(size / 5.5));
  const showImage = Boolean(url) && mode !== 'failed';

  const handleImageError = useCallback(
    () => setMode((current) => (current === 'cors' ? 'plain' : 'failed')),
    [],
  );

  const tileClassName = useMemo(
    () =>
      'grid shrink-0 place-items-center overflow-hidden' +
      (appear ? ' brand-mark-appear' : '') +
      (className ? ` ${className}` : ''),
    [appear, className],
  );

  const chipColor = chip?.color;
  const tileStyle = useMemo<CSSProperties>(
    () => ({
      width: size,
      height: size,
      borderRadius: corner,
      // No mark, or one we could not read: a flat dark tile with initials, never a fabricated white
      // square around nothing.
      backgroundColor: showImage ? chipColor ?? NEUTRAL_CHIP : '#242424',
      // The initials tile keeps its ring; `raised` adds the drop shadow on top of it, since an
      // inset and an outset shadow compose rather than replace one another.
      boxShadow:
        [showImage ? null : 'inset 0 0 0 1px #333333', raised ? BRAND_MARK_RAISED_SHADOW : null]
          .filter(Boolean)
          .join(', ') || undefined,
      animationDelay: appear && appearDelayMs ? `${appearDelayMs}ms` : undefined,
    }),
    [size, corner, showImage, chipColor, raised, appear, appearDelayMs],
  );

  const initialsStyle = useMemo<CSSProperties>(
    () => ({
      color: '#8A8A8A',
      fontWeight: 700,
      fontSize: Math.max(8, Math.round(size * 0.32)),
      letterSpacing: '-0.02em',
    }),
    [size],
  );

  return (
    <span className={tileClassName} style={tileStyle}>
      {showImage ? (
         
        <img
          // Remount on mode change so the browser re-requests without the CORS flag.
          key={mode}
          src={url ?? undefined}
          alt={name ? `${name} logo` : ''}
          // The same request serves the pixels the tile colour is measured from, so measuring costs
          // nothing extra. Dropped on the retry, see LoadMode.
          crossOrigin={mode === 'cors' ? 'anonymous' : undefined}
          onError={handleImageError}
          style={IMAGE_STYLE}
        />
      ) : (
        <span style={initialsStyle}>{initials}</span>
      )}
    </span>
  );
};

// Drawn once per row: a shortlist table paints fifty of these, and its props are all primitives.
export const BrandMark = memo(BrandMarkComponent);

BrandMark.displayName = 'BrandMark';
